import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './StoryQuests.css';
import StoryVideo from './StoryVideo';
import StoryTalk from './StoryTalk';
import { hasWatchedStoryVideo, markStoryVideoWatched, probeStoryVideo } from '../utils/storyVideo';
import { tl } from '../utils/inlineI18n';
import { getActiveLocale } from '../utils/localeFormat';

// Kızılırmak Sürgünleri hikâye görevleri paneli (görev penceresinin
// "Hikâye" sekmesi). Görevlerin içeriği backend/data/*_quests.json dosyalarından gelir.

const TR_NAMES = {
  category: { main: 'Ana görev', class: 'Karakter görevi', profession: 'Meslek', side: 'Yan görev', repeatable: 'Tekrarlanabilir' },
  cls: { savasci: 'Emekli Şövalye', sura: 'Saray Büyücüsü', asil: 'Asil', arabaci: 'Fakir Arabacı' },
  profession: { fishing: 'Balıkçılık', mining: 'Madencilik', farming: 'Tarım' },
  rep: { kral: 'Kral', halk: 'Halk', persler: 'Persler' },
  step: {
    kill: 'Savaş', boss: 'Boss', destroy: 'Göktaşı', choice: 'Seçim', question: 'Soru', statCheck: 'Kontrol',
    collect: 'Topla', ending: 'Son', talk: 'Konuş', cutscene: 'Sahne', goto: 'Git', interact: 'Etkileşim',
    deliver: 'Teslim', protect: 'Koru', puzzle: 'Bulmaca',
  },
};
const EN_NAMES = {
  category: { main: 'Main quest', class: 'Class quest', profession: 'Profession', side: 'Side quest', repeatable: 'Repeatable' },
  cls: { savasci: 'Retired Knight', sura: 'Court Sorcerer', asil: 'Noble', arabaci: 'Poor Carter' },
  profession: { fishing: 'Fishing', mining: 'Mining', farming: 'Farming' },
  rep: { kral: 'King', halk: 'People', persler: 'Persians' },
  step: {
    kill: 'Battle', boss: 'Boss', destroy: 'Meteorite', choice: 'Choice', question: 'Question', statCheck: 'Check',
    collect: 'Collect', ending: 'Ending', talk: 'Talk', cutscene: 'Scene', goto: 'Go', interact: 'Interact',
    deliver: 'Deliver', protect: 'Protect', puzzle: 'Puzzle',
  },
};
// Dil değişince güncel tablo kullanılsın diye her erişimde seçilir.
const names = (group) => tl(TR_NAMES[group], EN_NAMES[group]);
const CATEGORY_NAMES = new Proxy({}, { get: (_, k) => names('category')[k], ownKeys: () => Reflect.ownKeys(TR_NAMES.category), getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }) });
const CLASS_NAMES = new Proxy({}, { get: (_, k) => names('cls')[k] });
const PROFESSION_NAMES = new Proxy({}, { get: (_, k) => names('profession')[k] });
const REP_NAMES = new Proxy({}, { get: (_, k) => names('rep')[k], ownKeys: () => Reflect.ownKeys(TR_NAMES.rep), getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }) });
const STEP_TAGS = new Proxy({}, { get: (_, k) => names('step')[k] });
const WORLD_EFFECTS = {
  persianZonesSafe: () => tl('Pers bölgeleri güvenli', 'Persian zones are safe'),
  villagePricePercent: (v) => tl(`Köy fiyatları +%${v}`, `Village prices +${v}%`),
  dragonSummon: (v) => (v === 'daily'
    ? tl('Günde bir kez ejder çağırma', 'Summon the dragon once a day')
    : tl('Güçlü ejder çağırma', 'Powerful dragon summon')),
  persianAllies: () => tl('Pers birlikleri müttefik', 'Persian troops are allies'),
  hostileZonesRemoved: (v) => tl(`Artık düşman değil: ${[].concat(v).join(', ')}`, `No longer hostile: ${[].concat(v).join(', ')}`),
  bothSidesShops: () => tl('İki tarafın dükkânları açık', "Both sides' shops are open"),
  freeTravelPersianZones: () => tl('Pers bölgelerinde serbest dolaşım', 'Free travel in Persian zones'),
  taxPercent: (v) => tl(`Pers vergisi %${v}`, `Persian tax ${v}%`),
  safeZone: (v) => tl(`Güvenli bölge: ${v}`, `Safe zone: ${v}`),
  unlockQuestAtLevel: (v) => tl(`Lv ${v.level}'de gizli görev`, `Secret quest at Lv ${v.level}`),
  occupiedVillages: (v) => tl(`İşgal altında: ${[].concat(v).join(', ')}`, `Occupied: ${[].concat(v).join(', ')}`),
  unlockChain: (v) => tl(`Direniş görev zinciri (Lv ${v.minLevel}–${v.maxLevel})`, `Resistance quest chain (Lv ${v.minLevel}–${v.maxLevel})`),
  dragonBoss: (v) => tl(`Lv ${v.level}'de ejderi geri alma savaşı`, `Battle to reclaim the dragon at Lv ${v.level}`),
  palaceShopClosed: () => tl('Saray dükkânı kapalı', 'Palace shop is closed'),
  dragonWild: () => tl('Ejder müttefik ama vahşi', 'The dragon is an ally but wild'),
  marketTaxPercent: (v) => tl(`Pazar vergisi %${v}`, `Market tax ${v}%`),
  byzantineShop: () => tl('Bizans tüccarı açık', 'Byzantine merchant is open'),
  byzantineOutposts: () => tl('Köylerde Bizans karakolları', 'Byzantine outposts in villages'),
  villageDiscountPercent: (v) => tl(`Köylerde %${v} indirim`, `${v}% discount in villages`),
  freeFarmPlots: (v) => tl(`${v} ücretsiz tarla`, `${v} free farm plots`),
  villageMilitia: () => tl('Köy milisleri', 'Village militia'),
  harvestBonusPercent: (v) => tl(`Hasat +%${v}`, `Harvest +${v}%`),
};

const signed = (n) => (n > 0 ? `+${n}` : `−${Math.abs(n)}`);
const fmt = (n) => Number(n || 0).toLocaleString(getActiveLocale());

function effectChips(e, statNames, goldCost) {
  const out = [];
  if (!e) return out;
  Object.entries(e.stats || {}).forEach(([k, v]) => v && out.push({ text: `${signed(v)} ${statNames[k] || k}`, good: v > 0 }));
  if (e.maxHpPercent) out.push({ text: `Max HP ${signed(e.maxHpPercent)}%`, good: e.maxHpPercent > 0 });
  Object.entries(e.reputation || {}).forEach(([k, v]) => v && out.push({ text: `${REP_NAMES[k] || k} ${signed(v)}`, good: v > 0 }));
  if (e.gold) out.push({ text: `${signed(e.gold)} ${tl('altın', 'gold')}`, good: e.gold > 0 });
  if (goldCost) out.push({ text: `−${fmt(goldCost)} ${tl('altın', 'gold')}`, good: false });
  return out;
}

function Chips({ items }) {
  if (!items.length) return null;
  return (
    <span className="sq-chips">
      {items.map((c, i) => (
        <span key={i} className={`sq-chip ${c.good ? 'is-good' : 'is-bad'}`}>{c.text}</span>
      ))}
    </span>
  );
}

function rewardText(rewards, statNames) {
  const parts = [];
  if (rewards.exp) parts.push(`${fmt(rewards.exp)} exp`);
  if (rewards.gold) parts.push(`${fmt(rewards.gold)} ${tl('altın', 'gold')}`);
  (rewards.items || []).forEach((it) => parts.push(`${it.count}× ${it.name}`));
  Object.entries(rewards.stats || {}).forEach(([k, v]) => parts.push(`${signed(v)} ${statNames[k] || k} ${tl('(kalıcı)', '(permanent)')}`));
  if (rewards.chooseStat) {
    parts.push(
      `+${rewards.chooseStat.amount} ${tl('kalıcı stat, sen seç', 'permanent stat, your choice')}: ${rewards.chooseStat.options.map((k) => statNames[k] || k).join(' / ')}`
    );
  }
  Object.entries(rewards.reputation || {}).forEach(([k, v]) => parts.push(`${REP_NAMES[k] || k} ${signed(v)}`));
  return parts.join(' · ');
}

export function describeStoryResult(res, statNames, title) {
  if (!res) return '';
  const parts = [];
  if (res.exp) parts.push(`+${fmt(res.exp)} exp`);
  if (res.gold) parts.push(`${signed(res.gold)} ${tl('altın', 'gold')}`);
  (res.items || []).forEach((it) => parts.push(`${it.count}× ${it.name}`));
  Object.entries(res.stats || {}).forEach(([k, v]) => v && parts.push(`${signed(v)} ${statNames[k] || k}`));
  if (res.maxHpPercent) parts.push(`Max HP ${signed(res.maxHpPercent)}%`);
  Object.entries(res.reputation || {}).forEach(([k, v]) => v && parts.push(`${REP_NAMES[k] || k} ${signed(v)}`));
  if (res.profession) parts.push(`${PROFESSION_NAMES[res.profession] || res.profession} ${tl('açıldı', 'unlocked')}`);
  const msgs = (res.messages || []).join(' ');
  const body = [msgs, parts.join(', ')].filter(Boolean).join(' ');
  return body ? `${title ? `${title}: ` : ''}${body}` : '';
}

function useVideoAvailable(src) {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!src) {
      setOk(false);
      return undefined;
    }
    probeStoryVideo(src).then((v) => alive && setOk(v));
    return () => {
      alive = false;
    };
  }, [src]);
  return ok;
}

function VideoButton({ src, title, onWatch, compact }) {
  const ok = useVideoAvailable(src);
  if (!ok) return null;
  return (
    <button
      type="button"
      className={`auth-button auth-button--compact sq-btn${compact ? ' sq-btn--small' : ''}`}
      onClick={() => onWatch(src, title)}
    >
      {compact ? tl('İzle', 'Watch') : tl('Videoyu izle', 'Watch video')}
    </button>
  );
}

// O anki adımın düğme yazısı: konuşma, teslim, kontrol ya da eski türler için "Devam et".
function advanceLabel(q) {
  const cur = q.steps.find((s) => s.current);
  if (!cur) return tl('Devam et', 'Continue');
  if (cur.type === 'talk') return cur.npc ? tl(`${cur.npc} ile konuş`, `Talk to ${cur.npc}`) : tl('Konuş', 'Talk');
  if (cur.type === 'deliver') return tl(`Teslim et (${cur.count}/${cur.need})`, `Deliver (${cur.count}/${cur.need})`);
  if (cur.type === 'statCheck') return tl('Dene', 'Try');
  return tl('Devam et', 'Continue');
}

function penaltyChips(q, statNames) {
  const chips = effectChips(q.abandonPenalty || {}, statNames);
  return chips;
}

function QuestCard({ q, statNames, busy, act, onWatch }) {
  const available = q.status === 'available';
  const [confirm, setConfirm] = useState(null); // 'abandon' | 'decline' | null
  return (
    <li className={`item-market-product-card sq-quest sq-cat-${q.category}`}>
      <div className="sq-quest-head">
        <span className="sq-eyebrow">
          {CATEGORY_NAMES[q.category] || q.category} · {q.region} · Lv {q.minLevel}
          {q.npc ? ` · ${q.npc}` : ''}
        </span>
        <span className="item-market-product-card__name sq-title">{q.title}</span>
      </div>
      <p className="item-market-product-card__desc sq-story">{q.story}</p>
      {q.classText ? (
        <p className="sq-classtext">
          <span className="sq-label">{tl('Senin yolun', 'Your path')}</span> {q.classText}
        </p>
      ) : null}
      {q.variantText ? (
        <p className="sq-classtext">
          <span className="sq-label">{tl('Dünya değişti', 'The world changed')}</span> {q.variantText}
        </p>
      ) : null}
      {q.deadlineLevel > 0 ? (
        <p className="sq-deadline">{q.deadlineLevel}. seviyeye gelmeden bitir; yoksa Persler prensesi kaçırır.</p>
      ) : null}
      <ol className="sq-steps">
        {q.steps.map((s) => (
          <li key={s.id} className={`sq-step${s.done ? ' is-done' : ''}${s.current ? ' is-current' : ''}`}>
            <span className="sq-step-tag">{STEP_TAGS[s.type] || s.type}</span>
            <span className="sq-step-text">
              {s.text}
              {s.hint && (s.current || available) ? (
                <span className="sq-hint">
                  {' · '}
                  {s.type === 'talk' || s.type === 'deliver' ? s.hint : `${s.target}: ${s.hint}`}
                </span>
              ) : null}
            </span>
            {s.need > 1 && (s.current || s.done) ? (
              <span className="sq-step-count">
                {s.count}/{s.need}
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      {rewardText(q.rewards || {}, statNames) ? (
        <p className="item-market-product-card__price sq-rewards">{tl('Ödül', 'Reward')}: {rewardText(q.rewards || {}, statNames)}</p>
      ) : null}

      {q.question ? (
        <div className="sq-question">
          <p>{q.question.text}</p>
          <div className="sq-actions">
            {q.question.answers.map((a) => (
              <button key={a} type="button" className="auth-button auth-button--compact sq-btn" disabled={busy} onClick={() => act('answer', q, { answer: a })}>
                {a}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {q.needsChoice && q.choices ? (
        <div className="sq-choices">
          <span className="sq-label">{tl('Kararını ver', 'Make your choice')}</span>
          {q.choices.map((c) => (
            <button
              key={c.id}
              type="button"
              className="sq-choice"
              disabled={busy || c.locked}
              onClick={() => act('choose', q, { choiceId: c.id })}
              title={c.lockReason || ''}
            >
              <span className="sq-choice-text">{c.text}</span>
              {c.locked ? <span className="sq-lock">{c.lockReason}</span> : <Chips items={effectChips(c.effects, statNames, c.goldCost)} />}
            </button>
          ))}
        </div>
      ) : null}

      {q.canAdvance && q.actionBlocked ? <p className="sq-blocked">{q.actionBlocked}</p> : null}

      {confirm ? (
        <div className="sq-confirm" role="alertdialog" aria-label={confirm === 'abandon' ? tl('Görevi iptal et', 'Abandon quest') : tl('Görevi reddet', 'Decline quest')}>
          {confirm === 'abandon' ? (
            <>
              <p>
                {q.abandonRemoves
                  ? tl('İptal edersen bu görev listeden silinir ve bir daha alınamaz.', 'If you abandon it, this quest is removed and can never be taken again.')
                  : tl('İptal edersen ilerlemen sıfırlanır; görevi sonra yeniden alabilirsin.', 'If you abandon it, your progress resets; you can take the quest again later.')}
              </p>
              {penaltyChips(q, statNames).length ? (
                <p>
                  <span className="sq-label">{tl('Bedeli', 'Cost')}</span> <Chips items={penaltyChips(q, statNames)} />
                </p>
              ) : null}
            </>
          ) : (
            <p>{tl('Reddedersen bu görev bir daha gelmez. Bir bedeli yok.', 'If you decline, this quest will not come again. There is no cost.')}</p>
          )}
          <div className="sq-actions">
            <button
              type="button"
              className="auth-button auth-button--compact sq-btn is-danger"
              disabled={busy}
              onClick={() => {
                setConfirm(null);
                act(confirm, q);
              }}
            >
              {confirm === 'abandon' ? tl('Evet, iptal et', 'Yes, abandon') : tl('Evet, reddet', 'Yes, decline')}
            </button>
            <button type="button" className="auth-button auth-button--compact sq-btn" onClick={() => setConfirm(null)}>
              {tl('Vazgeç', 'Cancel')}
            </button>
          </div>
        </div>
      ) : null}

      <div className="sq-actions">
        {available ? (
          <button type="button" className="auth-button auth-button--compact sq-btn" disabled={busy} onClick={() => act('accept', q)}>
            {tl('Görevi al', 'Accept quest')}
          </button>
        ) : null}
        {q.canAdvance ? (
          <button
            type="button"
            className="auth-button auth-button--compact sq-btn"
            disabled={busy || Boolean(q.actionBlocked)}
            onClick={() => act('advance', q)}
          >
            {advanceLabel(q)}
          </button>
        ) : null}
        {q.canComplete ? (
          <button type="button" className="auth-button auth-button--compact sq-btn is-primary" disabled={busy} onClick={() => act('complete', q)}>
            {q.isFinal ? tl('Savaşı bitir ve sonu gör', 'End the war and see the ending') : tl('Görevi tamamla', 'Complete quest')}
          </button>
        ) : null}
        {q.video ? <VideoButton src={q.video} title={q.title} onWatch={onWatch} /> : null}
        {available && q.canDecline && !confirm ? (
          <button type="button" className="auth-button auth-button--compact sq-btn sq-btn--quiet" disabled={busy} onClick={() => setConfirm('decline')}>
            {tl('Reddet', 'Decline')}
          </button>
        ) : null}
        {!available && q.canAbandon && !confirm ? (
          <button type="button" className="auth-button auth-button--compact sq-btn sq-btn--quiet" disabled={busy} onClick={() => setConfirm('abandon')}>
            {tl('Görevi iptal et', 'Abandon quest')}
          </button>
        ) : null}
      </div>
      {!available && !q.canAbandon && q.abandonBlocked ? <p className="sq-muted sq-note">{tl('Karar verdiğin için bu görev iptal edilemez.', 'You made a choice, so this quest cannot be abandoned.')}</p> : null}
    </li>
  );
}

function StoryStatus({ view }) {
  const statNames = view.statNames || {};
  const stats = Object.entries(view.stats || {}).filter(([, v]) => v);
  const bag = Object.entries(view.inventory || {}).filter(([, n]) => n > 0);
  return (
    <div className="sq-status">
      <div className="sq-reps">
        {Object.keys(REP_NAMES).map((k) => {
          const v = view.reputation?.[k] ?? 0;
          return (
            <div key={k} className={`sq-rep sq-rep-${k}`}>
              <div className="sq-rep-top">
                <span>{REP_NAMES[k]}</span>
                <span className="sq-num">{v}</span>
              </div>
              <div className="sq-bar" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={v} aria-label={tl(`${REP_NAMES[k]} itibarı`, `${REP_NAMES[k]} reputation`)}>
                <span style={{ width: `${v}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="sq-line">
        <span className="sq-label">{tl('Karakter', 'Character')}</span> {CLASS_NAMES[view.class] || view.class}
        {(view.titles || []).length ? ` · ${view.titles.join(', ')}` : ''}
      </p>
      <p className="sq-line">
        <span className="sq-label">{tl('Kalıcı bonuslar', 'Permanent bonuses')}</span>{' '}
        {stats.length || view.maxHpPercent || view.damagePercent
          ? [
              ...stats.map(([k, v]) => `${signed(v)} ${statNames[k] || k}`),
              ...(view.maxHpPercent ? [`Max HP ${signed(view.maxHpPercent)}%`] : []),
              ...(view.damagePercent ? [`${tl('Hasar', 'Damage')} ${signed(view.damagePercent)}%`] : []),
            ].join(' · ')
          : tl('henüz yok', 'none yet')}
      </p>
      <p className="sq-line">
        <span className="sq-label">{tl('Meslekler', 'Professions')}</span>{' '}
        {(view.professions || []).length ? view.professions.map((p) => PROFESSION_NAMES[p] || p).join(', ') : tl('kapalı', 'locked')}
        {bag.length ? (
          <>
            {' · '}
            <span className="sq-label">{tl('Görev çantası', 'Quest bag')}</span> {bag.map(([k, n]) => `${n}× ${k}`).join(', ')}
          </>
        ) : null}
      </p>
    </div>
  );
}

const StoryQuests = ({ username, view, onStoryChange, onCharacterChange }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [log, setLog] = useState([]);
  const [tab, setTab] = useState(null);
  const [video, setVideo] = useState(null);
  const [talk, setTalk] = useState(null);
  const logId = useRef(1);

  const watch = useCallback((src, title) => setVideo({ src, title }), []);
  const closeVideo = useCallback(() => {
    setVideo((v) => {
      if (v) markStoryVideoWatched(username, v.src);
      return null;
    });
  }, [username]);

  const act = useCallback(
    async (action, q, extra = {}) => {
      if (!username || busy) return;
      setBusy(true);
      setError('');
      try {
        const res = await axios.post('/api/story/action', { username, action, questId: q?.id, ...extra });
        const data = res.data || {};
        if (!data.success) {
          setError(data.message || tl('İşlem yapılamadı', 'Action failed'));
          return;
        }
        onStoryChange?.(data.story, data.actionCount);
        const title = action === 'complete' ? tl(`${q?.title} tamamlandı`, `${q?.title} completed`) : action === 'resolveStat' ? tl('Kalıcı stat', 'Permanent stat') : q?.title;
        const line = describeStoryResult(data.result, data.story?.statNames || {}, title);
        if (line) setLog((l) => [{ id: logId.current++, text: line }, ...l].slice(0, 5));
        if (data.result?.dialogue?.length) setTalk({ speaker: data.result.speaker, lines: data.result.dialogue });
        if (action === 'accept') {
          setTab('active');
          // Ana görev alınınca videosu (eklenmişse ve daha önce izlenmediyse) açılır.
          if (q?.video && !hasWatchedStoryVideo(username, q.video)) {
            probeStoryVideo(q.video).then((ok) => ok && setVideo({ src: q.video, title: q.title }));
          }
        }
        if (action === 'complete' || action === 'abandon' || action === 'decline') setTab(null);
        if (data.result) onCharacterChange?.();
      } catch (e) {
        setError(e?.response?.data?.message || tl('Sunucuya ulaşılamadı', 'Could not reach the server'));
      } finally {
        setBusy(false);
      }
    },
    [username, busy, onStoryChange, onCharacterChange]
  );

  if (!view) return <div className="item-market-loading">{tl('Hikâye yükleniyor…', 'Loading story…')}</div>;
  const statNames = view.statNames || {};
  const current = tab || (view.active.length ? 'active' : 'available');
  const tabs = [
    ['active', `${tl('Aktif', 'Active')} (${view.active.length})`],
    ['available', `${tl('Alınabilir', 'Available')} (${view.available.length})`],
    ['upcoming', `${tl('Yakında', 'Upcoming')} (${view.upcoming.length})`],
    ['done', `${tl('Biten', 'Finished')} (${view.completed.length + view.failed.length})`],
  ];
  const list = current === 'active' ? view.active : current === 'available' ? view.available : [];

  return (
    <div className="sq-panel">
      {video ? <StoryVideo src={video.src} title={video.title} onClose={closeVideo} /> : null}
      {talk ? <StoryTalk speaker={talk.speaker} lines={talk.lines} onClose={() => setTalk(null)} /> : null}
      <StoryStatus view={view} />
      {error ? <p className="sq-error" role="alert">{error}</p> : null}
      {(view.events || []).map((e) => (
        <p key={e} className="sq-deadline">{e}</p>
      ))}
      {view.ending ? (
        <section className="sq-ending">
          <span className="sq-eyebrow">{tl('Hikâyenin sonu', 'The ending')}</span>
          <h3>{view.ending.title}</h3>
          <p>{view.ending.story}</p>
          <p>
            <span className="sq-label">{tl('Unvan', 'Title')}</span> {view.ending.permanentTitle}
            {Object.entries(view.ending.permanentStats || {}).map(([k, v]) => ` · ${signed(v)} ${statNames[k] || k}`)}
          </p>
          {Object.keys(view.ending.worldEffects || {}).filter((k) => k !== 'reputation').length ? (
            <ul className="sq-effects">
              {Object.entries(view.ending.worldEffects)
                .filter(([k]) => k !== 'reputation')
                .map(([k, v]) => (
                  <li key={k}>{WORLD_EFFECTS[k] ? WORLD_EFFECTS[k](v) : `${k}: ${JSON.stringify(v)}`}</li>
                ))}
            </ul>
          ) : null}
        </section>
      ) : null}
      {(view.pendingStats || []).map((p) => (
        <div key={p.id} className="item-market-product-card sq-pending">
          <p>
            <strong>{p.title}</strong>: +{p.amount} {tl('kalıcı stat kazandın. Hangisine verilsin?', 'permanent stat earned. Which one should get it?')}
          </p>
          <div className="sq-actions">
            {p.options.map((k) => (
              <button
                key={k}
                type="button"
                className="auth-button auth-button--compact sq-btn is-primary"
                disabled={busy}
                onClick={() => act('resolveStat', null, { pendingId: p.id, stat: k })}
              >
                +{p.amount} {statNames[k] || k}
              </button>
            ))}
          </div>
        </div>
      ))}
      {log.length ? (
        <ul className="sq-log" aria-live="polite">
          {log.map((l, i) => (
            <li key={l.id} className={i === 0 ? 'is-new' : ''}>
              {l.text}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="sq-tabs" role="tablist">
        {tabs.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={current === id} className={`sq-tab${current === id ? ' is-on' : ''}`} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>
      {current === 'active' || current === 'available' ? (
        list.length ? (
          <ul className="level-quests-list">
            {list.map((q) => (
              <QuestCard key={q.id} q={q} statNames={statNames} busy={busy} act={act} onWatch={watch} />
            ))}
          </ul>
        ) : (
          <p className="item-market-empty">
            {current === 'active' ? tl('Aktif hikâye görevin yok. Alınabilir sekmesinden bir görev al.', 'You have no active story quest. Take one from the Available tab.') : tl('Şu an alınabilecek görev yok. Seviye atladıkça yenileri açılır.', 'No quests available right now. New ones unlock as you level up.')}
          </p>
        )
      ) : null}
      {current === 'upcoming' ? (
        <ul className="sq-summary">
          {view.upcoming.map((q) => (
            <li key={q.id}>
              <span className="sq-num">Lv {q.minLevel}</span>
              <span>
                <strong>{q.title}</strong> <span className="sq-muted">· {CATEGORY_NAMES[q.category]} · {q.reason}</span>
              </span>
            </li>
          ))}
          {!view.upcoming.length ? <li className="sq-muted">{tl('Yaklaşan görev yok.', 'No upcoming quests.')}</li> : null}
        </ul>
      ) : null}
      {current === 'done' ? (
        <ul className="sq-summary">
          {view.completed.map((q) => (
            <li key={q.id}>
              <span className="sq-num">Lv {q.minLevel}</span>
              <span>
                <strong>{q.title}</strong>
                {q.choice ? <span className="sq-muted"> · {tl('Seçimin', 'Your choice')}: {q.choice}</span> : null}
              </span>
              {q.video ? <VideoButton src={q.video} title={q.title} onWatch={watch} compact /> : null}
            </li>
          ))}
          {view.failed.map((q) => (
            <li key={q.id} className="is-failed">
              <span className="sq-num">Lv {q.minLevel}</span>
              <span>
                <strong>{q.title}</strong>{' '}
                <span className="sq-muted">
                  · {q.status === 'abandoned' ? tl('iptal edildi', 'abandoned') : q.status === 'declined' ? tl('reddedildi', 'declined') : tl('başarısız', 'failed')}
                </span>
              </span>
            </li>
          ))}
          {!view.completed.length && !view.failed.length ? <li className="sq-muted">{tl('Henüz biten görev yok.', 'No finished quests yet.')}</li> : null}
        </ul>
      ) : null}
    </div>
  );
};

export default StoryQuests;
